import {FC, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  activateActiveCampaignIntegration,
  deactivateActiveCampaignIntegration,
  fetchActiveIntegrationApp,
  getActivateIntegrationByUser,
  getIntegrationRefetchDataStatus,
  resetSelectedIntegrationData,
} from '../IntegrationSlice/IntegrationSlice'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Form, Button, Accordion, Row, Col} from 'react-bootstrap'
import {isLoading} from '../../../../Redux/FrontEnd/Loading/LoadingSlice'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import {ExternalProvider} from '../IntegrationSlice/Types'
import {toast} from 'react-toastify'
import {useLazyGetSendersQuery} from '../../../../../services/cellcast.v2'
import Select from 'react-select'
import {
  useExportActiveCampaignContactMutation,
  useImportActiveCampaignContactMutation,
  useUpdateActiveCampaignSenderMutation,
} from '../../../../../services/cellcast'
import {SenderCamp} from '../../../../../services/cellcast.type'
import {getDefaultRegion} from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {REGION} from '../../../../Utils/Constant/ConstantForWebsite'
import {useLazyGetActiveSenderQuery} from '../../../../../services/cellcast.v3'

interface ActiveCampaignProps {
  selectedExternalProvider: ExternalProvider
  userData: any
}

const ActiveCampaign: FC<ActiveCampaignProps> = ({selectedExternalProvider, userData}) => {
  const userRegion = useSelector(getDefaultRegion)

  let userId = ''
  if (userData && userData.user) {
    if (userData.user._id !== userData.user.ownerId && !userData.user.subClientAccountId) {
      userId = userData.user.ownerId
    } else {
      userId = userData.user._id
    }
  }

  const selectedActiveIntegration = useSelector(
    getActivateIntegrationByUser(userId, selectedExternalProvider._id)
  )

  const refetch = useSelector(getIntegrationRefetchDataStatus)
  const globalLoading = useSelector(isLoading)

  const dispatch = useDispatch()

  const [selectedSmsSender, setSelectedSmsSender] = useState('#SharedNum#')
  const [selectedMmsSender, setSelectedMmsSender] = useState('#SharedNum#')

  const [selectedUsSmsSender, setSelectedUsSmsSender] = useState('')
  const [selectedUsMmsSender, setSelectedUsMmsSender] = useState('')

  // load sender options
  const [fetchSender, {data, isFetching: fetchSenderIsFetching, isLoading: fetchSenderIsLoading}] =
    useLazyGetSendersQuery()
  const smsOptions = data?.data?.sms || [{value: '#SharedNum#', label: '#SharedNum#'}]

  const [
    fetchRegionSender,
    {
      data: regionSenderData,
      isFetching: fetchRegionSenderIsFetching,
      isLoading: fetchRegionSenderIsLoading,
    },
  ] = useLazyGetActiveSenderQuery()

  const defaultRegionOptions = [{value: '', label: 'Select Sender ID'}]

  const regionSmsOptions =
    regionSenderData?.data?.data &&
    Array.isArray(regionSenderData?.data?.data) &&
    regionSenderData?.data?.data?.length > 0
      ? regionSenderData?.data?.data?.map((s: any) => ({
          value: s.audNumber,
          label: `${s.numberType} - ${s.audNumber}`,
        }))
      : []

  const regionMmsOptions =
    regionSenderData?.data?.data &&
    Array.isArray(regionSenderData?.data?.data) &&
    regionSenderData?.data?.data?.length > 0
      ? regionSenderData?.data?.data
          .filter((s: any) => s.type === 'mms')
          .map((s: any) => ({value: s.audNumber, label: `${s.numberType} - ${s.audNumber}`}))
      : []

  const [updateSmsSender, {isLoading: updateSmsSenderLoading}] = useUpdateActiveCampaignSenderMutation()
  const [updateMmsSender, {isLoading: updateMmsSenderLoading}] = useUpdateActiveCampaignSenderMutation()

  const [importContact, {isLoading: importContactLoading}] = useImportActiveCampaignContactMutation()
  const [exportContact, {isLoading: exportContactLoading}] = useExportActiveCampaignContactMutation()

  // Handle form
  const activateSchema = Yup.object().shape({
    apiUrl: Yup.string().required('API URL is required'),
    apiKey: Yup.string().required('API Key is required'),
  })

  const formik = useFormik({
    initialValues: {
      apiUrl: '',
      apiKey: '',
    },
    validationSchema: activateSchema,
    onSubmit: (values) => {
      dispatch(activateActiveCampaignIntegration(values)).then(() => {
        dispatch(fetchActiveIntegrationApp())
        dispatch(resetSelectedIntegrationData)
      })
    },
  })

  // load data when first load
  useEffect(() => {
    dispatch(resetSelectedIntegrationData)
    dispatch(fetchActiveIntegrationApp())
    if ([REGION.AU].includes(userRegion)) {
      fetchSender(null)
    } else {
      fetchRegionSender(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedActiveIntegration && selectedActiveIntegration.data) {
      if (selectedActiveIntegration.data.smsSender) {
        setSelectedSmsSender(selectedActiveIntegration.data.smsSender)
      }

      if (selectedActiveIntegration.data.mmsSender) {
        setSelectedMmsSender(selectedActiveIntegration.data.mmsSender)
      }

      if (selectedActiveIntegration.data.usSmsSender) {
        setSelectedUsSmsSender(selectedActiveIntegration.data.usSmsSender)
      }

      if (selectedActiveIntegration.data.usMmsSender) {
        setSelectedUsMmsSender(selectedActiveIntegration.data.usMmsSender)
      }
    }
  }, [selectedActiveIntegration])

  useEffect(() => {
    if (refetch) {
      dispatch(fetchActiveIntegrationApp())
    }
  }, [refetch])

  // handle delete
  const [isOpenDeleteConfirmation, setIsOpenDeleteConfirmation] = useState(false)
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)

  const onClickDeactivateHandler = () => {
    setIsOpenDeleteConfirmation(true)
  }

  const onUpdateSmsHandler = (region: string) => {
    let updatedSender
    switch (region) {
      case REGION.US:
        updatedSender = selectedUsSmsSender
        break
      default:
        updatedSender = selectedSmsSender
    }

    updateSmsSender({
      sender: updatedSender,
      type: SenderCamp.SMS,
      region: region.toLocaleLowerCase(),
    }).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
          dispatch(fetchActiveIntegrationApp())
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onUpdateMmsHandler = (region: string) => {
    let updatedSender
    switch (region) {
      case REGION.US:
        updatedSender = selectedUsMmsSender
        break
      default:
        updatedSender = selectedMmsSender
    }

    updateMmsSender({
      sender: updatedSender,
      type: SenderCamp.MMS,
      region: region.toLocaleLowerCase(),
    }).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
          dispatch(fetchActiveIntegrationApp())
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onImportContactHandler = () => {
    importContact(null).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const onExportContactHandler = () => {
    exportContact(null).then((res) => {
      if ('data' in res) {
        if (res.data?.error && Object.keys(res.data?.error).length > 0) {
          toast.error(res?.data?.message)
        } else {
          toast.success(res?.data?.message)
        }
      } else if ('error' in res) {
        toast.error(res.error)
      }
    })
  }

  const confirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenDeleteConfirmation}
        handleClose={() => setIsOpenDeleteConfirmation(false)}
        Message={'Are you sure, you want to deactivate integration ?'}
        confirm={() => setIsDeleteConfirm(true)}
        confirmBtnTitle='Yes, deactivate'
      />
    ),
    [isOpenDeleteConfirmation]
  )

  useEffect(() => {
    if (isDeleteConfirm === true) {
      dispatch(deactivateActiveCampaignIntegration()).then(() => {
        dispatch(resetSelectedIntegrationData)
        dispatch(fetchActiveIntegrationApp())
      })

      setIsDeleteConfirm(false)
      setIsOpenDeleteConfirmation(false)
    }
  }, [isDeleteConfirm])

  return (
    <div className='card p-7 pb-lg-4 p-lg-10'>
      {confirmation}
      <h1>Detail Integration</h1>
      <div className='row'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            <div className='me-7 mb-4'>
              <div className='symbol symbol-100px symbol-lg-160px symbol-fixed position-relative'>
                {selectedExternalProvider?.image === '' || selectedExternalProvider === undefined ? (
                  <span
                    className='squere1'
                    style={{
                      width: '100px',
                      height: '100px',
                    }}
                  >{`${
                    selectedExternalProvider?.name !== undefined &&
                    selectedExternalProvider?.name !== ''
                      ? selectedExternalProvider?.name.split('')?.[0]?.toUpperCase() ?? 'C'
                      : 'EP'
                  }`}</span>
                ) : (
                  <img src={selectedExternalProvider.image} alt='activecampaign' />
                )}
                <div
                  className={`position-absolute translate-middle bottom-0 start-100 mb-6 rounded-circle border border-4 border-white h-20px w-20px ${
                    selectedActiveIntegration ? ' bg-success ' : 'bg-danger'
                  }`}
                ></div>
              </div>
            </div>
            <div className='flex-grow-1'>
              <span className='text-gray-800 fs-2 fw-bolder me-1 mb-10'>
                {selectedExternalProvider?.name ?? 'Undefined'}
              </span>
              {selectedActiveIntegration ? (
                <div className='mt-5'>
                  <Form.Group className='mb-10'>
                    <Form.Label>API URL</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.data?.activecampaign?.apiUrl || ''}
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>API Key</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.data?.activecampaign?.apiKey || ''}
                    />
                  </Form.Group>
                  <Form.Group className='mb-10'>
                    <Form.Label>Cellcast App Key</Form.Label>
                    <Form.Control
                      type='text'
                      disabled
                      value={selectedActiveIntegration?.data?.activecampaign?.appKey || ''}
                    />
                  </Form.Group>

                  {[REGION.AU].includes(userRegion) ? (
                    <>
                      <Form.Group className='mb-10'>
                        <Form.Label>SMS sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchSenderIsFetching || fetchSenderIsLoading}
                              onChange={(e) => setSelectedSmsSender(e?.value || '')}
                              value={
                                smsOptions.find((el) => el.value === selectedSmsSender) ||
                                smsOptions[0]
                              }
                              options={[...smsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateSmsSenderLoading}
                              onClick={() => onUpdateSmsHandler(REGION.AU)}
                            >
                              {updateSmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>
                    </>
                  ) : null}

                  {[REGION.US].includes(userRegion) ? (
                    <>
                      <Form.Group className='mb-10'>
                        <Form.Label>Sms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchRegionSenderIsFetching || fetchRegionSenderIsLoading}
                              onChange={(e) => setSelectedUsSmsSender(e?.value || '')}
                              value={
                                [...defaultRegionOptions, ...regionSmsOptions].find(
                                  (el: any) => el.value === selectedUsSmsSender
                                ) || defaultRegionOptions
                              }
                              options={[...defaultRegionOptions, ...regionSmsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateSmsSenderLoading}
                              onClick={() => onUpdateSmsHandler(userRegion)}
                            >
                              {updateSmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>

                      <Form.Group className='mb-10'>
                        <Form.Label>Mms sender for conversation api</Form.Label>
                        <Row>
                          <Col sm='6'>
                            <Select
                              isSearchable={false}
                              isDisabled={fetchRegionSenderIsFetching || fetchRegionSenderIsLoading}
                              onChange={(e) => setSelectedUsMmsSender(e?.value || '')}
                              value={
                                [...defaultRegionOptions, ...regionMmsOptions].find(
                                  (el: any) => el.value === selectedUsMmsSender
                                ) || defaultRegionOptions
                              }
                              options={[...defaultRegionOptions, ...regionMmsOptions]}
                              name='message'
                              className={`common-select `}
                              placeholder='Custom-LandingPage'
                            />
                          </Col>
                          <Col sm='4'>
                            <Button
                              variant='info'
                              type='submit'
                              size='sm'
                              disabled={updateMmsSenderLoading}
                              onClick={() => onUpdateMmsHandler(userRegion)}
                            >
                              {updateMmsSenderLoading ? (
                                <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                              ) : (
                                'Configure'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form.Group>
                    </>
                  ) : null}

                  <Accordion className='mb-10'>
                    <Accordion.Item eventKey='0'>
                      <Accordion.Header>Integration Step</Accordion.Header>
                      <Accordion.Body>
                        <div className='row mb-10'>
                          <div className='mb-5' style={{fontSize: 14}}>
                            <ol>
                              <li>Log in to your ActiveCampaign account</li>
                              <li>Go to Settings &gt; Developer</li>
                              <li>Copy your API URL and API Key</li>
                              <li>
                                <a
                                  href='https://developers.activecampaign.com/reference/authentication'
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  ActiveCampaign API Documentation
                                </a>
                              </li>
                            </ol>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <span className='text-gray-800 fs-3 fw-bolder me-1 mt-10'>Sync Contact</span>

                  <Form.Group className='mb-5 mt-3'>
                    <Row>
                      <Col sm='6'>
                        <Form.Label>Import contacts from ActiveCampaign</Form.Label>
                      </Col>
                      <Col sm='4'>
                        <Button
                          variant='info'
                          type='submit'
                          size='sm'
                          disabled={importContactLoading}
                          onClick={() => onImportContactHandler()}
                        >
                          {importContactLoading ? (
                            <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                          ) : (
                            'Import'
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form.Group>
                  {[REGION.AU].includes(userRegion) ? (
                    <Form.Group className='mb-5'>
                      <Row>
                        <Col sm='6'>
                          <Form.Label>Push contacts to ActiveCampaign</Form.Label>
                        </Col>
                        <Col sm='4'>
                          <Button
                            variant='info'
                            type='submit'
                            size='sm'
                            disabled={exportContactLoading}
                            onClick={() => onExportContactHandler()}
                          >
                            {exportContactLoading ? (
                              <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                            ) : (
                              'Push Contacts'
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form.Group>
                  ) : null}

                  <Button
                    variant='danger'
                    type='submit'
                    disabled={globalLoading}
                    onClick={() => onClickDeactivateHandler()}
                  >
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Deactivate'
                    )}
                  </Button>
                </div>
              ) : (
                <Form onSubmit={formik.handleSubmit} className='form mt-5'>
                  <Form.Group className='mb-10' controlId='apiUrl'>
                    <Form.Label>API URL</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your ActiveCampaign API URL (e.g. https://youraccount.api-us1.com)'
                      {...formik.getFieldProps('apiUrl')}
                      isInvalid={formik.touched.apiUrl && !!formik.errors.apiUrl}
                    />
                    {formik.touched.apiUrl && formik.errors.apiUrl && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.apiUrl}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className='text-muted'>
                      Found in Settings &gt; Developer in your ActiveCampaign account
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className='mb-10' controlId='apiKey'>
                    <Form.Label>API Key</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter your ActiveCampaign API Key'
                      {...formik.getFieldProps('apiKey')}
                      isInvalid={formik.touched.apiKey && !!formik.errors.apiKey}
                    />
                    {formik.touched.apiKey && formik.errors.apiKey && (
                      <Form.Control.Feedback type='invalid'>
                        {formik.errors.apiKey}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className='text-muted'>
                      Found in Settings &gt; Developer in your ActiveCampaign account
                    </Form.Text>
                  </Form.Group>
                  <Accordion className='mb-10'>
                    <Accordion.Item eventKey='0'>
                      <Accordion.Header>Integration Step</Accordion.Header>
                      <Accordion.Body>
                        <div className='row mb-10'>
                          <div className='mb-5' style={{fontSize: 14}}>
                            <ol>
                              <li>Log in to your ActiveCampaign account</li>
                              <li>Go to Settings &gt; Developer</li>
                              <li>Copy your API URL and API Key</li>
                              <li>Paste them in the fields above</li>
                              <li>
                                <a
                                  href='https://developers.activecampaign.com/reference/authentication'
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  ActiveCampaign API Documentation
                                </a>
                              </li>
                            </ol>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                  <Button variant='primary' type='submit' disabled={globalLoading}>
                    {globalLoading ? (
                      <span className='spinner-border spinner-border-xl align-middle ms-2'></span>
                    ) : (
                      'Activate'
                    )}
                  </Button>
                </Form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {ActiveCampaign}
